import {
  FormsConfigType,
  InputFieldType,
  NewWorkflowWidgetConfig,
  ProjectFormsConfig,
  WidgetNames,
  WorkflowConfig,
  WorkflowPageConfig,
  WorkflowZoneConfig,
  WorkflowZoneDevicesConfig,
} from '@finxone-platform/shared/sys-config-types';

function getFormKey(form: { id: string; parentFormID?: string }): string {
  return `${form.parentFormID || 'root'}:${form.id}`;
}

/**
 * Recursively builds project forms from all widgets in the workflow configuration.
 * Handles root forms and child forms (group/array) with recursive nesting.
 */
export function rebuildProjectForms(config: WorkflowConfig): ProjectFormsConfig[] {
  if (!config?.permissionSections?.roles) return config?.permissionSections?.projectForms ?? [];

  const discoveredForms = new Map<string, ProjectFormsConfig>();

  // 1. Initialize with existing forms to preserve orphaned ones
  registerExistingForms(config.permissionSections.projectForms ?? [], discoveredForms);

  // 2. Scan all widgets in the config to aggregate inputs and metadata
  scanAllWidgets(config, discoveredForms);

  // 3. Build the tree from discovered forms
  return buildFormTree(discoveredForms);
}

/**
 * Populates the discoveredForms map with existing forms.
 */
function registerExistingForms(
  forms: ProjectFormsConfig[],
  discoveredForms: Map<string, ProjectFormsConfig>,
) {
  for (const form of forms) {
    const key = getFormKey(form);
    discoveredForms.set(key, {
      ...form,
      // Some persisted configs have accumulated stray nulls in fieldsConfig (e.g. from an older
      // bug) - strip them here, the single place existing fieldsConfig data enters the rebuild, so
      // they don't keep getting carried forward on every rebuild and crash consumers that read
      // field.id without a null check (see generateListOfInputsOnOtherPages).
      fieldsConfig: (form.fieldsConfig ?? []).filter((field) => field != null),
      childForms: [], // Will be rebuilt
    });
    if (form.childForms) registerExistingForms(form.childForms, discoveredForms);
  }
}

/**
 * Scans all widgets across roles, zones, devices, and pages.
 */
function scanAllWidgets(config: WorkflowConfig, discoveredForms: Map<string, ProjectFormsConfig>) {
  for (const role of config.permissionSections.roles) {
    if (!role.zones) continue;
    for (const zone of Object.values(role.zones)) {
      const subscription = zone?.canvases?.subscription?.['default'] as WorkflowZoneDevicesConfig;
      if (!subscription) continue;

      const devices: (keyof WorkflowZoneDevicesConfig)[] = ['mobile', 'tablet', 'desktop'];
      for (const device of devices) {
        const zoneConfig: WorkflowZoneConfig | null = subscription[device];
        if (!zoneConfig) continue;

        for (const page of zoneConfig.pages || []) {
          const workflowPage = page as WorkflowPageConfig;
          traverseAndProcessWidgets(workflowPage.widgets || [], discoveredForms);
          traverseAndProcessWidgets(workflowPage.footerWidgets || [], discoveredForms);
        }
      }
    }
  }
}

/**
 * Recursively traverses widgets to find and process FORM_WIDGETs.
 */
function traverseAndProcessWidgets(
  widgets: NewWorkflowWidgetConfig[],
  discoveredForms: Map<string, ProjectFormsConfig>,
) {
  if (!Array.isArray(widgets)) return;
  for (const widget of widgets) {
    if (widget.name === WidgetNames.FORM_WIDGET) {
      processFormWidget(widget, discoveredForms);
    }

    if (widget.childComponents) {
      if (Array.isArray(widget.childComponents[0])) {
        for (const childArray of widget.childComponents as NewWorkflowWidgetConfig[][]) {
          traverseAndProcessWidgets(childArray, discoveredForms);
        }
      } else {
        traverseAndProcessWidgets(widget.childComponents as NewWorkflowWidgetConfig[], discoveredForms);
      }
    }
  }
}

/**
 * Extracts form metadata and inputs from a single FORM_WIDGET.
 */
function processFormWidget(
  widget: NewWorkflowWidgetConfig,
  discoveredForms: Map<string, ProjectFormsConfig>,
) {
  const formsConfig = widget.widgetConfiguration?.attributes?.widgetProperties
    ?.formsConfig as FormsConfigType;
  if (!formsConfig?.formID) return;

  const { formID, formType, childKey } = formsConfig;
  const isRoot = !formType || formType === 'root';
  const effectiveParentFormID = isRoot ? formsConfig.parentFormID : formID;
  const effectiveId = isRoot ? formID : (childKey ?? formID);

  const key = getFormKey({ id: effectiveId, parentFormID: effectiveParentFormID });

  let formEntry = discoveredForms.get(key);
  if (!formEntry) {
    formEntry = {
      id: effectiveId,
      fieldsConfig: [],
      childForms: [],
      formType: formType || 'root',
      parentFormID: effectiveParentFormID,
    };
    discoveredForms.set(key, formEntry);
  } else {
    // Update metadata if not already set correctly
    formEntry.formType = formType || formEntry.formType || 'root';
    formEntry.parentFormID = effectiveParentFormID || formEntry.parentFormID;
    formEntry.id = effectiveId || formEntry.id;
    formEntry.fieldsConfig ??= [];
  }

  // Aggregate inputs
  const widgetInputs = getInputsFromWidget(widget);
  for (const input of widgetInputs) {
    const existingIds = new Set(formEntry.fieldsConfig.map((f) => f?.id));
    if (!existingIds.has(input.id)) {
      formEntry.fieldsConfig.push(input);
    }
  }
}

/**
 * Recursively collects INPUT_WIDGET fields from a widget's children.
 */
function getInputsFromWidget(widget: NewWorkflowWidgetConfig): ProjectFormsConfig['fieldsConfig'] {
  const inputs: ProjectFormsConfig['fieldsConfig'] = [];
  const seenIds = new Set<string>();

  const collect = (children: NewWorkflowWidgetConfig[] | NewWorkflowWidgetConfig[][] | undefined) => {
    if (!Array.isArray(children)) return;
    for (const child of children) {
      if (Array.isArray(child)) {
        collect(child);
        continue;
      }
      if (child?.name === WidgetNames.INPUT_WIDGET) {
        const inputFieldConfig = child.widgetConfiguration?.attributes?.widgetProperties?.inputFieldConfig;
        if (inputFieldConfig?.id && !seenIds.has(inputFieldConfig.id)) {
          seenIds.add(inputFieldConfig.id);
          inputs.push({
            id: inputFieldConfig.id,
            type: inputFieldConfig.type as InputFieldType,
            label: inputFieldConfig.label,
            name: inputFieldConfig.name,
          });
        }
      }
      if (child?.childComponents) collect(child.childComponents);
    }
  };
  collect(widget.childComponents);
  return inputs;
}

/**
 * Builds the nested form tree from a flat map of discovered forms.
 */
function buildFormTree(discoveredForms: Map<string, ProjectFormsConfig>): ProjectFormsConfig[] {
  const allForms = Array.from(discoveredForms.values());
  const rootForms: ProjectFormsConfig[] = [];

  // First pass: identify root forms
  for (const form of allForms) {
    if (form.formType === 'root' || !form.parentFormID) {
      rootForms.push(form);
    }
  }

  // Second pass: attach children to parents with multiple passes for deep nesting
  let changed = true;
  const attached = new Set<string>();
  while (changed) {
    changed = false;
    for (const form of allForms) {
      const key = getFormKey(form);
      if (form.formType === 'root') continue;
      if (!form.parentFormID) continue;
      if (attached.has(key)) continue;
      const parent = findFormRecursive(rootForms, form.parentFormID);
      if (parent) {
        parent.childForms ??= [];
        const childFormIds = new Set(parent.childForms.map((f) => f.id));
        if (!childFormIds.has(form.id)) {
          parent.childForms.push(form);
          attached.add(key);
          changed = true;
        }
      }
    }
  }

  // Third pass: handle children whose parents were not found (treat as root to avoid data loss)
  for (const form of allForms) {
    const key = getFormKey(form);
    if (form.formType !== 'root' && form.parentFormID && !attached.has(key)) {
      const rootFormIds = new Set(
        rootForms.map((f) => {
          if (f.id === form.id && f.parentFormID === form.parentFormID) return f.id;
          return '';
        }),
      );
      if (!rootFormIds.has(form.id)) {
        rootForms.push(form);
      }
    }
  }
  return rootForms;
}

/**
 * Finds a form by ID and parent ID in a nested ProjectFormsConfig array.
 */
export function findFormRecursive(
  forms: ProjectFormsConfig[],
  formID: string,
  parentFormID?: string,
): ProjectFormsConfig | undefined {
  for (const form of forms) {
    if (form.id === formID && (parentFormID === undefined || form.parentFormID === parentFormID)) return form;
    if (form.childForms) {
      const found = findFormRecursive(form.childForms, formID, parentFormID);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Flattens a nested ProjectFormsConfig array into a single flat array.
 */
export function flattenProjectForms(forms: ProjectFormsConfig[]): ProjectFormsConfig[] {
  const result: ProjectFormsConfig[] = [];
  const recurse = (list: ProjectFormsConfig[]) => {
    for (const form of list) {
      result.push(form);
      if (form.childForms) recurse(form.childForms);
    }
  };
  recurse(forms);
  return result;
}
